import { Inject, Injectable } from '@angular/core';
import moment from 'moment';
import { ENVIRONMENT_CONFIG, EnvironmentConfig } from '@ipreo/northstar';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { mergeMap } from 'rxjs/operators';
import { MuniV3Schema } from './deal.service.types';
import { BasicDeal } from '../types/deal.types';
import { PlatformType } from '../types/muni-environment';

@Injectable()
export class CalendarService {
  private readonly apiHost: string;
  private readonly platform: PlatformType;

  private readonly platforms: { [key: string]: any } = {
    [PlatformType.polaris]: {
      mapper: (data) => this.mapper(data.issues),
      apiRoute: '/api/v1/PublicDeals',
    },
    [PlatformType.buyside]: {
      mapper: this.mapper,
      apiRoute: '/v1/deals/fixed-income/municipal/street-calendar',
    },
  };

  constructor(
    private http: HttpClient,
    @Inject(ENVIRONMENT_CONFIG) config: EnvironmentConfig
  ) {
    this.platform = config.params.platform as PlatformType;
    this.apiHost = config.params.apiHost as string;
  }

  private mapper(data: any[]): BasicDeal[] {
    return data.map(MuniV3Schema.toBasicDeal);
  }

  public getDealsById({
    weekOf,
    dealIds,
  }: {
    weekOf: string;
    dealIds: string[];
  }): Observable<BasicDeal[] | Error> {
    if (!dealIds || !dealIds.length) {
      return of(new Error('Specify deal ids to update'));
    }

    if (this.platform === PlatformType.buyside) {
      const url = `${this.apiHost}${
        this.platforms[this.platform].apiRoute
      }/live-updates`;

      const params = { ids: dealIds.join(',') };

      return this.httpGet(url, params);
    } else {
      return this.getDeals(weekOf);
    }
  }

  public getDeals(pSaleDateFrom: string): Observable<BasicDeal[] | Error> {
    const params: any = {
      pSaleDateFrom: moment(pSaleDateFrom).isoWeekday(1).format('YYYY-MM-DD'),
    };
    const url = `${this.apiHost}${this.platforms[this.platform].apiRoute}`;

    return this.httpGet(url, params);
  }

  private httpGet(
    url: string,
    params: { [key: string]: string }
  ): Observable<BasicDeal[] | Error> {
    return this.http
      .get(url, {
        observe: 'response',
        params,
      })
      .pipe(
        mergeMap((response: HttpResponse<any[]>) => {
          const data = response.status === 200 && response.body;

          return data
            ? of(this.platforms[this.platform].mapper(data))
            : throwError(Error('No deals'));
        })
      );
  }
}
